/**
 * TableSeedAffordance — the "Query a table" glyph that appears in the gutter
 * (or as an inline pill, following the same `showInlineExecuteBar` setting
 * as Execute / Peek / Edit) when the caret sits on a BLANK line outside any
 * statement. It fills the one spot the edit pencil leaves empty: with nothing
 * under the caret there is nothing to edit, so the same gutter slot offers
 * "start from a table" instead.
 *
 * On an entirely empty buffer a ghost hint sits to the right of the glyph so
 * a brand-new tab explains itself without docs. Both the glyph and the hint
 * open the picker.
 *
 * Subscribes to caret moves on its own (`onDidChangeCursorPosition`) so the
 * heavy host tab does not re-render per caret move; the host only supplies
 * the statement range under the caret (to hide inside statements) and the
 * enabled gate.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, Table2 } from 'lucide-react';

import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';

export const TABLE_SEED_TITLE = 'Query a table…';

interface TableSeedAffordanceProps {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  /** Master gate: VQE beta on, connected, SQL engine, nothing executing. */
  readonly enabled: boolean;
  /** Range of the statement under the caret (null = caret outside every statement). */
  readonly cursorStatementRange: EditorSelectionRange | null;
  /** Whether the user has an active text selection (the glyph hides). */
  readonly hasSelection: boolean;
  /** Buffer is empty / whitespace-only → show the ghost hint. */
  readonly isEmptyBuffer: boolean;
  /** `true` → inline pill (label + icon); `false` → square gutter glyph. */
  readonly inline: boolean;
  readonly onOpen: (anchorTop: number) => void;
}

type Layout = { top: number; left: number; lineHeight: number };

function isBlankLine(editor: SqlMonacoHandle, lineNumber: number): boolean {
  const lines = (editor.getValue() ?? '').split('\n');
  const text = lines[lineNumber - 1];
  return text === undefined || text.trim().length === 0;
}

export function TableSeedAffordance({
  editorRef,
  enabled,
  cursorStatementRange,
  hasSelection,
  isEmptyBuffer,
  inline,
  onOpen,
}: TableSeedAffordanceProps) {
  const [layout, setLayout] = useState<Layout | null>(null);

  const compute = useCallback((): Layout | null => {
    if (!enabled || hasSelection) return null;
    const editor = editorRef.current;
    if (!editor) return null;
    const caret = editor.getCursorPosition();
    if (!caret) return null;
    const line = caret.lineNumber;
    if (
      cursorStatementRange &&
      line >= cursorStatementRange.startLineNumber &&
      line <= cursorStatementRange.endLineNumber
    ) {
      return null;
    }
    if (!isBlankLine(editor, line)) return null;
    const pos = editor.getPixelPositionForPosition(line, 1);
    if (!pos) return null;
    const height = editor.getLayoutHeight();
    if (height !== null && (pos.top < 0 || pos.top > height)) return null;
    return { top: pos.top, left: pos.left, lineHeight: pos.height };
  }, [enabled, hasSelection, editorRef, cursorStatementRange]);

  // The Monaco chunk loads lazily, so on a brand-new tab the handle (or its
  // inner editor) is not ready when this effect first runs — and nothing else
  // re-renders an empty tab. Poll briefly before subscribing (cheap: one ref
  // read per tick, stops the moment the editor is up or the glyph is disabled).
  const [editorTick, setEditorTick] = useState(0);
  useEffect(() => {
    setLayout(compute());
    if (!enabled) return undefined;
    const editor = editorRef.current;
    // `getCursorPosition()` answers null until the inner Monaco instance is
    // up — subscribing before that returns no-op disposers.
    if (!editor || editor.getCursorPosition() === null) {
      const timer = setTimeout(() => setEditorTick((t) => t + 1), 150);
      return () => clearTimeout(timer);
    }
    const refresh = () => setLayout(compute());
    const offCursor = editor.onDidChangeCursorPosition(refresh);
    const offScroll = editor.onDidScrollChange(refresh);
    const offLayout = editor.onDidLayoutChange(refresh);
    return () => {
      offCursor();
      offScroll();
      offLayout();
    };
  }, [enabled, editorRef, compute, editorTick]);

  if (!layout) return null;

  const buttonSize = layout.lineHeight;
  const iconSize = Math.max(8, Math.round(buttonSize * 0.5));
  const open = () => onOpen(layout.top);

  const glyph = (
    <span className="relative inline-flex" style={{ height: iconSize, width: iconSize }}>
      <Table2 style={{ height: iconSize, width: iconSize }} />
      <Plus
        className="absolute -right-1 -bottom-1 rounded-full bg-background text-primary"
        style={{ height: Math.round(iconSize * 0.6), width: Math.round(iconSize * 0.6) }}
        strokeWidth={3}
      />
    </span>
  );

  return (
    <div
      className="absolute z-20 pointer-events-auto flex items-center"
      style={{ top: layout.top, left: 2 }}
      data-testid="table-seed-affordance"
      data-variant={inline ? 'inline' : 'gutter'}
    >
      {inline ? (
        <button
          type="button"
          onClick={open}
          className="flex items-center gap-1.5 rounded-md border border-border bg-background/85 px-2 text-xs font-medium text-foreground shadow-md backdrop-blur-sm transition-colors hover:bg-accent"
          style={{ height: buttonSize }}
          data-testid="table-seed-button"
          title={TABLE_SEED_TITLE}
          aria-label={TABLE_SEED_TITLE}
        >
          {glyph}
          Query a table
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          style={{ height: buttonSize, width: buttonSize }}
          className="flex items-center justify-center rounded-sm border border-border bg-background/85 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-accent"
          data-testid="table-seed-button"
          title={TABLE_SEED_TITLE}
          aria-label={TABLE_SEED_TITLE}
        >
          {glyph}
        </button>
      )}
      {isEmptyBuffer && !inline ? (
        <button
          type="button"
          onClick={open}
          className="ml-1 cursor-pointer whitespace-nowrap rounded px-1 text-xs italic text-muted-foreground/70 hover:text-foreground"
          style={{ height: buttonSize, marginLeft: Math.max(4, layout.left - buttonSize - 2) }}
          data-testid="table-seed-hint"
        >
          Query a table, or start typing
        </button>
      ) : null}
    </div>
  );
}
